/**
 * How a tracker board decides whether a row matches a filter, shared by the
 * board (which hides rows) and the automation engine (whose rule conditions
 * must keep exactly the rows the same filter would show).
 *
 * Nothing here reads a clock or a time zone. "Today" arrives as a `YYYY-MM-DD`
 * string: the board passes the viewer's day, the engine the rule author's, and
 * the process zone of either side never enters into it.
 */
export const DATE_BUCKETS = ['overdue', 'this-month', 'later'];
export const FILTER_OPERATORS = ['is', 'is-not'];
const KIND_BY_COLUMN_TYPE = {
    status: 'status',
    people: 'people',
    date: 'date',
    date_range: 'date',
};
/** `null` for a type whose values are not worth enumerating (text, number, …). */
export function filterKindForColumnType(type) {
    return KIND_BY_COLUMN_TYPE[type] ?? null;
}
const isRange = (raw) => typeof raw === 'object' && raw !== null && !Array.isArray(raw);
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;
/**
 * The calendar day a stored date names, or `null` when it names none. Read off
 * the string rather than through `Date`, which would place a bare date at UTC
 * midnight and move it a day for anyone west of Greenwich.
 */
export function isoDay(value) {
    const match = value ? ISO_DAY.exec(value) : null;
    if (!match)
        return null;
    const [, year, month, day] = match.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    const real = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
    return real ? match[0] : null;
}
/**
 * Which of the three date buckets a stored date falls in, or `null` when there
 * is no usable date. A range buckets on its END: the deadline is what makes an
 * item overdue, even though sorting orders ranges by their start.
 */
export function dateBucket(raw, todayIso) {
    const iso = typeof raw === 'string' ? raw : isRange(raw) ? (raw.end ?? raw.start) : undefined;
    const day = isoDay(iso);
    if (!day)
        return null;
    if (day < todayIso)
        return 'overdue';
    return day.slice(0, 7) > todayIso.slice(0, 7) ? 'later' : 'this-month';
}
/** A stored value as ids: every entry of a people array, or the lone string. */
export function storedValueIds(raw) {
    if (Array.isArray(raw))
        return raw.filter((v) => typeof v === 'string');
    return typeof raw === 'string' && raw ? [raw] : [];
}
/** The value ids one row matches on for one column. Empty = matches nothing. */
export function rowValueIds(raw, kind, todayIso) {
    if (kind !== 'date')
        return storedValueIds(raw);
    const bucket = dateBucket(raw, todayIso);
    return bucket ? [bucket] : [];
}
/**
 * Conditions narrowed to columns that STILL exist. A condition on a deleted
 * column would otherwise fail every row, with nothing left on screen to clear
 * it. One with no values chosen yet is a half-written condition, not a demand
 * that every row match nothing, so it is dropped as well.
 */
export function compileConditions(conditions, columns) {
    const kinds = new Map(columns.map((c) => [c.id, c.kind]));
    return conditions.flatMap((condition) => {
        const kind = kinds.get(condition.columnId);
        if (!kind || condition.values.length === 0)
            return [];
        return [
            {
                columnId: condition.columnId,
                kind,
                values: new Set(condition.values),
                negate: condition.operator === 'is-not',
            },
        ];
    });
}
/** Whether one cell passes one filter. */
export function matchesFilter(raw, filter, todayIso) {
    const hit = rowValueIds(raw, filter.kind, todayIso).some((id) => filter.values.has(id));
    return filter.negate ? !hit : hit;
}
